import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchDeviceCategories, fetchDeviceTypes, fetchPalcareDeviceTypes, publishLearnIn, pollLearnInDevices } from '../api/devicesApi.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import HeaderInfoTip from '../../../components/v2/HeaderInfoTip.jsx';
import Drawer from '../../../components/v2/Drawer.jsx';

// Default Nurse Call category filters (Inovonics)
const NC_AREA_CATEGORY_IDS = new Set([1, 3, 8, 9, 11, 12, 15, 16, 17]);
const NC_RESIDENT_CATEGORY_IDS = new Set([2, 11]);

const DEFAULT_CATEGORY_CONFIG = {
  area: NC_AREA_CATEGORY_IDS,
  resident: NC_RESIDENT_CATEGORY_IDS,
};

// Manufacturer IDs
const INOVONICS_ID = 1;
const PALCARE_ID = 4;

// Serial patterns
const SERIAL_PATTERN_NUMERIC = /^\d+$/;
const SERIAL_PATTERN_ALPHANUM = /^[A-Za-z0-9]+$/;
const INOVONICS_SERIAL_MIN_LENGTH = 4;

/**
 * Normalize a PalCare MAC-style serial (device_type_id = 1):
 * lowercase, strip hyphens and colons.  V1 Resident.jsx line 1206.
 */
function normalizePalcareSerial(serial, deviceTypeId) {
  if (String(deviceTypeId) === '1') {
    return serial.toLowerCase().replaceAll('-', '').replaceAll(':', '');
  }
  return serial;
}

/**
 * Strip leading zeros from an all-numeric serial. Returns "0" when the input
 * is entirely zeros so we never emit an empty string. Inputs that contain
 * non-digit characters are returned unchanged (validation handles those).
 */
function stripLeadingZerosNumeric(serial) {
  if (!/^\d+$/.test(serial)) return serial;
  const stripped = serial.replace(/^0+/, '');
  return stripped === '' ? '0' : stripped;
}

/**
 * Get the type-id key for a device-type record.
 * Inovonics types use `inovonics_type_id`; PalCare types use `device_type_id`.
 */
function getTypeId(typeRecord, manufacturerId) {
  return manufacturerId === PALCARE_ID
    ? typeRecord.device_type_id
    : typeRecord.inovonics_type_id;
}

/**
 * AddDeviceDrawer — slide-out drawer for adding a new device.
 *
 * Follows the same pattern as ServerDrawer.jsx: always-render with `is-open`
 * class toggle, form reset on open, Escape key close, focus first input.
 *
 * Supports multiple manufacturers via the `manufacturers` prop. When more than
 * one manufacturer is listed, a toggle is shown at the top. Each manufacturer
 * may have different form behaviour:
 *   - Inovonics (1): area/resident toggle, category, type, model, serial (numeric), alarm behavior
 *   - PalCare  (4): no base toggle (always resident), no category, type, model, serial (alphanumeric), no alarm behavior
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (formData: object, transType: string) => Promise<void>,
 *   existingDevices: Array,
 *   campusId: number,
 *   categoryConfig?: { area?: Set<number>, resident?: Set<number> },
 *   title?: string,
 *   manufacturers?: Array<{ id: number, label: string }>,
 *   sessionId?: string,
 *   roleWeight?: number,
 *   allowMultiple?: boolean,
 * }} props
 */
const AddDeviceDrawer = ({
  isOpen,
  onClose,
  onSave,
  existingDevices,
  campusId,
  categoryConfig = DEFAULT_CATEGORY_CONFIG,
  title = 'Add Device',
  manufacturers,
  sessionId = '',
  roleWeight = 0,
  allowMultiple = false,
  onBatchComplete,
  initialBase,
}) => {
  const drawerRef = useRef(null);

  // ---- Manufacturer support ----
  const mfrList = useMemo(() => manufacturers || [{ id: INOVONICS_ID, label: 'Inovonics' }], [manufacturers]);
  const showMfrToggle = mfrList.length > 1;
  const defaultMfrId = mfrList[0]?.id ?? INOVONICS_ID;

  const [manufacturerId, setManufacturerId] = useState(defaultMfrId);
  const isPalCare = manufacturerId === PALCARE_ID;

  // Derive available bases from categoryConfig keys (Inovonics only)
  const availableBases = useMemo(() => Object.keys(categoryConfig), [categoryConfig]);
  const showBaseToggle = !isPalCare && availableBases.length > 1;
  // Callers arriving from an area context (e.g. the Advanced Device Setup link
  // in the area quick-add flow) can preselect the Area side of the toggle so the
  // user doesn't have to flip it back after being sent here. Ignored when the
  // requested base isn't offered by categoryConfig.
  const defaultBase = (initialBase && availableBases.includes(initialBase))
    ? initialBase
    : (availableBases.includes('resident') ? 'resident' : (availableBases[0] || 'resident'));

  // ---- Form state ----
  const [deviceBase, setDeviceBase] = useState(defaultBase);

  /**
   * Whether this add will produce an area device. Hoisted out of handleSubmit so
   * the "Location within area" field is shown by exactly the same condition that
   * decides is_area_device — the two can't drift apart. PalCare devices are
   * always resident-side, so they never get the field.
   */
  const isAreaDevice = !isPalCare && deviceBase === 'area';

  const [categoryId, setCategoryId] = useState('');
  const [deviceTypeId, setDeviceTypeId] = useState('');
  const [modelId, setModelId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [description, setDescription] = useState('');
  const [useId, setUseId] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  // ---- Multi-add state ----
  const [multiMode, setMultiMode] = useState(false);
  const [serials, setSerials] = useState([]);
  const [newSerial, setNewSerial] = useState('');
  const [serialError, setSerialError] = useState('');
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchFailures, setBatchFailures] = useState([]);

  // ---- Listen-in state (Inovonics only) ----
  const [listening, setListening] = useState(false);
  const [listenCount, setListenCount] = useState(0);
  const [listenData, setListenData] = useState([]);
  const [listenDevice, setListenDevice] = useState('');
  const [listenPCode, setListenPCode] = useState(0);
  const [showDeviceList, setShowDeviceList] = useState(false);

  // ---- Reference data ----
  const [categories, setCategories] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);

  // ---- Helpers ----
  const resetFormFields = useCallback(() => {
    setCategoryId('');
    setDeviceTypeId('');
    setModelId('');
    setSerialNumber('');
    setDescription('');
    setUseId('');
    // Reset listen-in state
    setListening(false);
    setListenCount(0);
    setListenData([]);
    setListenDevice('');
    setListenPCode(0);
    setShowDeviceList(false);
    // Reset multi-add state
    setMultiMode(false);
    setSerials([]);
    setNewSerial('');
    setSerialError('');
    setBatchProgress({ current: 0, total: 0 });
    setBatchFailures([]);
  }, []);

  const loadCategories = useCallback((base) => {
    const allowedIds = categoryConfig[base];
    if (!allowedIds) return;
    setCategoriesLoading(true);
    fetchDeviceCategories(campusId, base)
      .then((data) => (data || []).filter((c) => allowedIds.has(c.device_category_id)))
      .then((filtered) => setCategories(filtered))
      .catch(() => setCategories([]))
      .finally(() => setCategoriesLoading(false));
  }, [campusId, categoryConfig]);

  const loadDeviceTypes = useCallback((mfrId) => {
    setTypesLoading(true);
    const fetcher = mfrId === PALCARE_ID
      ? fetchPalcareDeviceTypes(campusId)
      : fetchDeviceTypes(campusId);
    fetcher
      .then((data) => setDeviceTypes(data || []))
      .catch(() => setDeviceTypes([]))
      .finally(() => setTypesLoading(false));
  }, [campusId]);

  // ---- Reset + load on open ----
  useEffect(() => {
    if (isOpen) {
      const mfrId = defaultMfrId;
      setManufacturerId(mfrId);
      setDeviceBase(defaultBase);
      resetFormFields();
      setSaving(false);
      setCategories([]);
      setDeviceTypes([]);

      if (mfrId !== PALCARE_ID) {
        loadCategories(defaultBase);
      }
      loadDeviceTypes(mfrId);
    }
  }, [isOpen, campusId, defaultBase, defaultMfrId, loadCategories, loadDeviceTypes, resetFormFields]);

  // ---- Escape closes the image preview first; the Drawer shell closes the drawer ----
  useEffect(() => {
    if (!isOpen || !previewOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setPreviewOpen(false);
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, previewOpen]);

  // ---- Focus first input on open ----
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // ---- Manufacturer change ----
  const handleManufacturerChange = useCallback((newId) => {
    if (newId === manufacturerId) return;
    setManufacturerId(newId);
    resetFormFields();
    setCategories([]);
    setDeviceTypes([]);

    // Reload reference data for new manufacturer
    if (newId !== PALCARE_ID) {
      setDeviceBase(defaultBase);
      loadCategories(defaultBase);
    }
    loadDeviceTypes(newId);
  }, [manufacturerId, defaultBase, loadCategories, loadDeviceTypes, resetFormFields]);

  // ---- Re-fetch categories when deviceBase changes (Inovonics only) ----
  const handleDeviceBaseChange = useCallback((newBase) => {
    if (newBase === deviceBase) return;
    setDeviceBase(newBase);
    setCategoryId('');
    setDeviceTypeId('');
    setModelId('');
    setUseId('');
    // The location field only exists on the area side; dropping it here keeps a
    // value typed for an area from riding along on a resident add.
    setDescription('');
    loadCategories(newBase);
  }, [deviceBase, loadCategories]);

  // ---- Reset cascading fields on category change ----
  const handleCategoryChange = useCallback((val) => {
    setCategoryId(val);
    setDeviceTypeId('');
    setModelId('');
    setUseId('');
  }, []);

  // ---- Reset cascading fields on type change ----
  const handleTypeChange = useCallback((val) => {
    setDeviceTypeId(val);
    setModelId('');
    setUseId('');
  }, []);

  // ---- Filtered types ----
  const filteredTypes = useMemo(() => {
    if (isPalCare) {
      // PalCare: show all types (no category filter needed)
      return [...deviceTypes].sort((a, b) =>
        (a.device_type_name || '').localeCompare(b.device_type_name || '')
      );
    }
    // Inovonics: filter by selected category
    if (!categoryId) return [];
    return deviceTypes
      .filter((t) => String(t.device_category_id) === String(categoryId))
      .sort((a, b) => (a.device_type_name || '').localeCompare(b.device_type_name || ''));
  }, [deviceTypes, categoryId, isPalCare]);

  // ---- Selected type's models and uses ----
  const selectedType = useMemo(() => {
    if (!deviceTypeId) return null;
    return deviceTypes.find(
      (t) => String(getTypeId(t, manufacturerId)) === String(deviceTypeId)
    ) || null;
  }, [deviceTypes, deviceTypeId, manufacturerId]);

  const previewImageUrl = selectedType?.image_url || '';
  useEffect(() => {
    setPreviewError(false);
    if (!previewImageUrl) setPreviewOpen(false);
  }, [previewImageUrl]);

  const models = useMemo(() => selectedType?.models || [], [selectedType]);

  const uses = useMemo(() => {
    if (isPalCare) return []; // PalCare has no alarm behaviors
    return (selectedType?.uses || []).filter((u) => u.option_number !== 0);
  }, [selectedType, isPalCare]);

  // Includes option_number=0 so single-behavior types still get a useId.
  const allUses = useMemo(() => {
    if (isPalCare) return [];
    return selectedType?.uses || [];
  }, [selectedType, isPalCare]);

  // ---- Auto-select first model when models become available ----
  useEffect(() => {
    if (models.length > 0 && !modelId) {
      setModelId(String(models[0].model_id));
    }
  }, [models, modelId]);

  // ---- Auto-select first use when uses become available ----
  useEffect(() => {
    if (allUses.length > 0 && !useId) {
      setUseId(String(allUses[0].inovonics_device_use_id));
    }
  }, [allUses, useId]);

  // ---- Listen-in: derived values ----
  const selectedModel = useMemo(() => {
    if (!modelId) return null;
    return models.find((m) => String(m.model_id) === String(modelId)) || null;
  }, [models, modelId]);

  const productCode = selectedModel?.product_type_id || 0;
  const msgClass = selectedModel?.message_class || 0;

  const allProductCodes = useMemo(
    () => models.map((m) => m.product_type_id).filter(Boolean),
    [models],
  );

  const listenEnabled = useMemo(() => {
    if (isPalCare) return false;
    return !!modelId && !!sessionId;
  }, [isPalCare, modelId, sessionId]);

  // ---- Listen-in: start handler ----
  const handleStartListen = useCallback(() => {
    if (!listenEnabled) return;
    setListening(true);
    setListenCount(0);
    setListenDevice('');
    setListenPCode(0);
    setListenData([]);
    setShowDeviceList(true);

    // Higher-authority users listen for the specific product code; others listen
    // across the type's codes. The old server-version gate (>= 4.04.14) is gone
    // — every campus is past that version.
    const useSpecific = iAmHigherAuthority(80, roleWeight);
    const sendProductType = useSpecific ? [productCode] : allProductCodes;

    publishLearnIn({
      campusId,
      clientId: sessionId,
      productType: sendProductType,
      msgClass,
    }).then(([ok]) => {
      if (!ok) {
        setListening(false);
        setShowDeviceList(false);
      }
    });
  }, [listenEnabled, productCode, allProductCodes, msgClass, campusId, sessionId, roleWeight]);

  // ---- Listen-in: stop handler ----
  const handleStopListen = useCallback(() => {
    setListening(false);
    setShowDeviceList(false);
  }, []);

  // ---- Multi-add: toggle handler (also stops any active listen-in) ----
  const handleToggleMultiMode = useCallback(() => {
    setMultiMode((prev) => {
      const next = !prev;
      if (next) {
        setListening(false);
        setShowDeviceList(false);
        setListenData([]);
        setListenDevice('');
        setListenPCode(0);
        setListenCount(0);
        setSerialNumber('');
        setSerialError('');
        setBatchFailures([]);
      } else {
        setSerials([]);
        setNewSerial('');
        setSerialError('');
        setBatchFailures([]);
      }
      return next;
    });
  }, []);

  // ---- Listen-in: polling timer (60s countdown, poll every 4s) ----
  useEffect(() => {
    if (!listening) return;

    const interval = setInterval(() => {
      setListenCount((prev) => {
        const next = prev + 1;
        if (next >= 60) {
          setListening(false);
          return 60;
        }
        if (next % 4 === 0) {
          pollLearnInDevices({ campusId, clientId: sessionId }).then((data) =>
            setListenData(data || []),
          );
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [listening, campusId, sessionId]);

  // ---- Listen-in: auto-select first discovered device ----
  useEffect(() => {
    if (listenData.length > 0 && !listenDevice) {
      setListenDevice(String(listenData[0]?.trx_id || ''));
      setListenPCode(listenData[0]?.pType || 0);
    }
  }, [listenData, listenDevice]);

  // ---- Serial validation ----
  const serialPattern = isPalCare ? SERIAL_PATTERN_ALPHANUM : SERIAL_PATTERN_NUMERIC;

  // PalCare Pendants (manufacture 4, device_type 1) must have a serial that
  // begins with "PC" (case-insensitive). normalizePalcareSerial lowercases the
  // string for device_type_id=1, so the post-normalization prefix is "pc".
  const isPalCarePendant = isPalCare && String(deviceTypeId) === '1';
  const palcarePendantPrefixOk = (s) => /^pc/i.test(s);

  const normalizedSerial = useMemo(() => {
    const trimmed = serialNumber.trim();
    if (!trimmed) return '';
    if (isPalCare) return normalizePalcareSerial(trimmed, deviceTypeId);
    // Inovonics (manufacture_id = 1): all-numeric serials drop leading zeros
    // before save / dedupe so "00123" and "123" map to the same device row.
    return stripLeadingZerosNumeric(trimmed);
  }, [serialNumber, isPalCare, deviceTypeId]);

  // Inovonics serials must additionally be at least 4 characters (numeric).
  const inovonicsLengthOk = (s) => s.length >= INOVONICS_SERIAL_MIN_LENGTH;

  const isSerialValid =
    normalizedSerial !== '' &&
    serialPattern.test(normalizedSerial) &&
    (!isPalCarePendant || palcarePendantPrefixOk(normalizedSerial)) &&
    (isPalCare || inovonicsLengthOk(normalizedSerial));

  // ---- Duplicate check (uses listener trx_id in listen mode, normalized serial otherwise) ----
  const duplicateDevice = useMemo(() => {
    const candidate = showDeviceList ? String(listenDevice || '') : normalizedSerial;
    if (!candidate) return null;
    return (existingDevices || []).find(
      (d) => String(d.manufacture_uuid) === candidate
    ) || null;
  }, [normalizedSerial, listenDevice, showDeviceList, existingDevices]);

  // ---- Multi-add: normalize + validate a candidate serial ----
  const normalizeCandidate = useCallback((raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    if (isPalCare) return normalizePalcareSerial(trimmed, deviceTypeId);
    // Inovonics: drop leading zeros for numeric serials (see normalizedSerial).
    return stripLeadingZerosNumeric(trimmed);
  }, [isPalCare, deviceTypeId]);

  const handleAddSerial = useCallback(() => {
    const candidate = normalizeCandidate(newSerial);
    if (!candidate) {
      setSerialError('Enter a serial number');
      return;
    }
    if (!serialPattern.test(candidate)) {
      setSerialError(isPalCare
        ? 'Enter a valid alphanumeric serial number (no spaces)'
        : 'Enter a valid numeric serial number (digits only, no spaces)');
      return;
    }
    if (!isPalCare && !inovonicsLengthOk(candidate)) {
      setSerialError(`Serial number must be at least ${INOVONICS_SERIAL_MIN_LENGTH} digits`);
      return;
    }
    if (isPalCarePendant && !palcarePendantPrefixOk(candidate)) {
      setSerialError('Serial number must begin with PC');
      return;
    }
    if (serials.includes(candidate)) {
      setSerialError('That serial number is already in the list');
      return;
    }
    const exists = (existingDevices || []).find((d) => String(d.manufacture_uuid) === candidate);
    if (exists) {
      setSerialError('A device with that serial number already exists in the system');
      return;
    }
    setSerials((prev) => [...prev, candidate]);
    setNewSerial('');
    setSerialError('');
  }, [newSerial, normalizeCandidate, serialPattern, isPalCare, isPalCarePendant, serials, existingDevices]);

  const handleRemoveSerial = useCallback((s) => {
    setSerials((prev) => prev.filter((item) => item !== s));
  }, []);

  // ---- Form validity ----
  const serialReady = multiMode
    ? serials.length > 0
    : showDeviceList ? listenDevice !== '' : isSerialValid;
  const duplicateReady = multiMode ? true : !duplicateDevice;

  const isFormValid =
    (isPalCare || categoryId) &&
    deviceTypeId &&
    modelId &&
    serialReady &&
    (uses.length === 0 || useId) &&
    duplicateReady &&
    !saving;

  // ---- Submit ----
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isFormValid) return;

      const transType = isAreaDevice ? 'device_add_area' : 'device_add_resident';

      const buildFormData = (finalSerial) => ({
        campus_id: campusId,
        manufacture_id: manufacturerId,
        manufacture_uuid: finalSerial,
        device_name: selectedType?.device_type_name || '',
        device_use_id: useId ? parseInt(useId, 10) : 0,
        is_active: 0,
        is_alerting_now: false,
        is_server: false,
        is_area_device: isAreaDevice ? 1 : 0,
        is_resident: false,
        area_id: 0,
        resident_id: 0,
        manufacture_model_id: parseInt(modelId, 10),
        escalation_enabled: true,
        // Belt-and-braces: the field is only rendered for area devices, and the
        // base toggle clears it, but never send placement text on a resident add.
        descriptions: isAreaDevice ? description.trim() : '',
        device_type_id: parseInt(deviceTypeId, 10),
      });

      setSaving(true);

      if (multiMode) {
        setBatchFailures([]);
        setBatchProgress({ current: 0, total: serials.length });
        const failures = [];
        const total = serials.length;
        for (let i = 0; i < total; i += 1) {
          const s = serials[i];
          setBatchProgress({ current: i + 1, total });
          // eslint-disable-next-line no-await-in-loop
          const result = await onSave(buildFormData(s), transType, { silent: true });
          const ok = Array.isArray(result) ? result[0] : !!result;
          if (!ok) {
            const err = Array.isArray(result) ? result[1] : 'Failed to add device';
            failures.push({ serial: s, error: err || 'Failed to add device' });
          }
        }
        setSaving(false);
        setBatchProgress({ current: 0, total: 0 });
        const added = total - failures.length;
        if (typeof onBatchComplete === 'function') {
          onBatchComplete({ added, failed: failures.length, total });
        }
        if (failures.length === 0) {
          onClose();
        } else {
          setBatchFailures(failures);
          setSerials(failures.map((f) => f.serial));
        }
        return;
      }

      // Single-add path (unchanged)
      const finalSerial = showDeviceList ? listenDevice : normalizedSerial;
      onSave(buildFormData(finalSerial), transType).finally(() => setSaving(false));
    },
    [isFormValid, isPalCare, isAreaDevice, deviceBase, campusId, manufacturerId, normalizedSerial, showDeviceList, listenDevice, selectedType, useId, modelId, description, deviceTypeId, onSave, multiMode, serials, onBatchComplete, onClose]
  );

  // ---- Sort categories alphabetically ----
  const sortedCategories = useMemo(() =>
    [...categories].sort((a, b) =>
      (a.device_category_short_name || '').localeCompare(b.device_category_short_name || '', undefined, { sensitivity: 'base' })
    ),
  [categories]);

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={onClose}
        panelRef={drawerRef}
        ariaLabel={title}
        headerStart={(
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 className="dp-drawer__title">{title}</h2>
            <HeaderInfoTip
              spaced
              title="Adding Devices"
              body="Devices added to the system start as unassigned. To activate a device it must be assigned to a room or resident, which can be done via the Community > Residents or Community > Areas section of the portal."
            />
          </div>
        )}
        rawBody
      >
        {/* Form */}
        <form onSubmit={handleSubmit} className="dp-drawer__body">

          {/* Manufacturer toggle — only when multiple manufacturers */}
          {showMfrToggle ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Manufacturer <span className="dp-drawer__required">*</span></label>
              <div className="dp-toggle-group">
                {mfrList.map((mfr) => (
                  <button
                    key={mfr.id}
                    type="button"
                    onClick={() => handleManufacturerChange(mfr.id)}
                    className={`dp-toggle-btn ${manufacturerId === mfr.id ? 'is-active' : ''}`}
                  >
                    {mfr.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* Device Base toggle (Area / Resident) — Inovonics with multiple bases only */}
          {showBaseToggle ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">Device Base <span className="dp-drawer__required">*</span></label>
              <div className="dp-toggle-group">
                {availableBases.includes('area') ? (
                  <button
                    type="button"
                    onClick={() => handleDeviceBaseChange('area')}
                    className={`dp-toggle-btn ${deviceBase === 'area' ? 'is-active' : ''}`}
                  >
                    Area
                  </button>
                ) : null}
                {availableBases.includes('resident') ? (
                  <button
                    type="button"
                    onClick={() => handleDeviceBaseChange('resident')}
                    className={`dp-toggle-btn ${deviceBase === 'resident' ? 'is-active' : ''}`}
                  >
                    Resident
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Device Category — Inovonics only (PalCare skips this) */}
          {!isPalCare ? (
            <div className="dp-drawer__field">
              <label htmlFor="ad-category" className="dp-drawer__label">
                Device Category <span className="dp-drawer__required">*</span>
              </label>
              <select
                id="ad-category"
                value={categoryId}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="dp-drawer__select"
                disabled={categoriesLoading}
                required
              >
                <option value="">
                  {categoriesLoading ? 'Loading categories...' : 'Select category...'}
                </option>
                {sortedCategories.map((c) => (
                  <option key={c.device_category_id} value={c.device_category_id}>
                    {c.device_category_short_name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Device Type */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-type" className="dp-drawer__label">
              Device Type <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="ad-type"
              value={deviceTypeId}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="dp-drawer__select"
              disabled={(!isPalCare && !categoryId) || typesLoading}
              required
            >
              <option value="">
                {typesLoading
                  ? 'Loading types...'
                  : !isPalCare && !categoryId
                    ? 'Select a category first...'
                    : 'Select device type...'}
              </option>
              {filteredTypes.map((t) => (
                <option key={getTypeId(t, manufacturerId)} value={getTypeId(t, manufacturerId)}>
                  {t.device_type_name}
                </option>
              ))}
            </select>
          </div>

          {/* Model */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-model" className="dp-drawer__label">
              Model <span className="dp-drawer__required">*</span>
            </label>
            <select
              id="ad-model"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="dp-drawer__select"
              disabled={!deviceTypeId || models.length === 0}
              required
            >
              <option value="">
                {!deviceTypeId ? 'Select a device type first...' : 'Select model...'}
              </option>
              {models.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_name}
                </option>
              ))}
            </select>
          </div>

          {/* Device-type image preview — click to open lightbox */}
          {previewImageUrl && !previewError ? (
            <div className="dp-drawer__device-preview">
              <button
                type="button"
                className="dp-drawer__device-preview-thumb"
                onClick={() => setPreviewOpen(true)}
                aria-label={`Enlarge image of ${selectedType?.device_type_name || 'device'}`}
              >
                <img
                  src={`/static/image/${previewImageUrl}`}
                  alt={selectedType?.device_type_name || ''}
                  onError={() => setPreviewError(true)}
                />
              </button>
            </div>
          ) : null}

          {/* Add Multiple Devices toggle — gated by allowMultiple prop */}
          {allowMultiple ? (
            <div className="dp-drawer__field dp-drawer__field--row">
              <input
                id="ad-multi"
                type="checkbox"
                className="dp-checkbox"
                checked={multiMode}
                disabled={listening || saving}
                onChange={handleToggleMultiMode}
              />
              <label htmlFor="ad-multi" className="dp-drawer__label dp-drawer__label--inline">
                Add Multiple Devices
              </label>
            </div>
          ) : null}

          {/* Serial Number — Manual input, Listen-in dropdown, or Multi-add chip list */}
          <div className="dp-drawer__field">
            <label htmlFor="ad-serial" className="dp-drawer__label">
              {multiMode ? 'Serial Numbers' : 'Serial Number'} <span className="dp-drawer__required">*</span>
            </label>

            {multiMode ? (
              <>
                {serials.length > 0 ? (
                  <div className="dp-serial-list">
                    {serials.map((s) => (
                      <span key={s} className="dp-serial-chip">
                        {s}
                        <button
                          type="button"
                          className="dp-serial-chip__remove"
                          onClick={() => handleRemoveSerial(s)}
                          aria-label={`Remove ${s}`}
                          disabled={saving}
                        >
                          {'\u2715'}
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="dp-serial-add-row">
                  <input
                    id="ad-serial"
                    type="text"
                    value={newSerial}
                    onChange={(e) => { setNewSerial(e.target.value); setSerialError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSerial(); } }}
                    placeholder={isPalCare ? 'Enter serial number (alphanumeric)' : 'Enter device serial number'}
                    className={`dp-drawer__input ${serialError ? 'is-invalid' : ''}`}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="dp-btn dp-btn--sm dp-btn--secondary"
                    onClick={handleAddSerial}
                    disabled={saving}
                  >
                    Add
                  </button>
                </div>
                {serialError ? (
                  <p className="dp-drawer__error">{serialError}</p>
                ) : null}
              </>
            ) : !showDeviceList ? (
              <>
                <input
                  id="ad-serial"
                  type="text"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder={isPalCare ? 'Enter serial number (alphanumeric)' : 'Enter device serial number'}
                  className={`dp-drawer__input ${(serialNumber && !isSerialValid) || duplicateDevice ? 'is-invalid' : ''}`}
                  required={!showDeviceList}
                />
                {serialNumber && !isSerialValid ? (
                  <p className="dp-drawer__error">
                    {isPalCarePendant && serialPattern.test(normalizedSerial) && !palcarePendantPrefixOk(normalizedSerial)
                      ? 'Serial number must begin with PC'
                      : !isPalCare && serialPattern.test(normalizedSerial) && !inovonicsLengthOk(normalizedSerial)
                        ? `Serial number must be at least ${INOVONICS_SERIAL_MIN_LENGTH} digits`
                        : isPalCare
                          ? 'Enter a valid alphanumeric serial number (no spaces)'
                          : 'Enter a valid numeric serial number (digits only, no spaces)'}
                  </p>
                ) : null}
                {duplicateDevice ? (
                  <p className="dp-drawer__error">A device with this serial number already exists in the system</p>
                ) : null}
              </>
            ) : (
              /* Listen-in device dropdown (shown during/after listen) */
              <>
                <select
                  id="ad-serial-listen"
                  value={listenDevice ? `${listenDevice},${listenPCode}` : ''}
                  onChange={(e) => {
                    const parts = e.target.value.split(',');
                    setListenDevice(parts[0] || '');
                    setListenPCode(parseInt(parts[1], 10) || 0);
                  }}
                  className={`dp-drawer__select ${duplicateDevice ? 'is-invalid' : ''}`}
                  required={showDeviceList}
                >
                  {listenData.length === 0 ? (
                    <option value="">{listening ? 'Listening for devices...' : 'No devices found'}</option>
                  ) : (
                    listenData.map((d) => (
                      <option key={d.trx_id} value={`${d.trx_id},${d.pType}`}>
                        {d.name} ({d.trx_id})
                      </option>
                    ))
                  )}
                </select>
                {duplicateDevice ? (
                  <p className="dp-drawer__error">A device with this serial number already exists in the system</p>
                ) : null}
              </>
            )}

            {/* Auto Listen / Stop Listen buttons — Inovonics only, hidden in multi mode but with disabled wrapper */}
            {!isPalCare && sessionId ? (
              <div className="dp-listen-actions">
                {!showDeviceList ? (
                  <span className="dp-disabled-tooltip-wrap">
                    <button
                      type="button"
                      className="dp-btn dp-btn--sm dp-btn--primary"
                      disabled={!listenEnabled || multiMode}
                      onClick={handleStartListen}
                      title={!multiMode && !listenEnabled ? 'Select a model first to enable auto listen' : undefined}
                    >
                      <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.788m13.788 0c3.808 3.808 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                      Auto Listen
                    </button>
                    {multiMode ? (
                      <span className="dp-disabled-tooltip" role="tooltip">
                        Listen-in is not available when adding multiple devices.
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={`dp-btn dp-btn--sm ${listening ? 'dp-btn--danger' : 'dp-btn--secondary'}`}
                    onClick={handleStopListen}
                  >
                    <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z" />
                    </svg>
                    {listening ? 'Stop Listen' : 'Cancel'}
                  </button>
                )}
              </div>
            ) : null}

            {/* Listen progress bar */}
            {listening ? (
              <div className="dp-listen-progress">
                <div
                  className="dp-listen-progress__bar"
                  style={{ width: `${(listenCount / 60) * 100}%` }}
                />
                <span className="dp-listen-progress__text">
                  {listenData.length > 0
                    ? `${listenData.length} device${listenData.length !== 1 ? 's' : ''} found — ${60 - listenCount}s remaining`
                    : `Listening... ${60 - listenCount}s remaining`}
                </span>
              </div>
            ) : null}
          </div>

          {/* Location within area (optional) — area devices only. A resident
              device is identified by the person it belongs to, so placement text
              has nothing to describe there. */}
          {isAreaDevice ? (
          <div className="dp-drawer__field">
            <label htmlFor="ad-description" className="dp-drawer__label">
              Location within area
            </label>
            <input
              id="ad-description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional... Bedroom, Bathroom, etc."
              className="dp-drawer__input"
            />
          </div>
          ) : null}

          {/* Alarm Behavior (uses) — Inovonics only, when type has use options.
              Rendered as a sectioned-off radio group: each option is a fully
              clickable row with the radio top-aligned to its (often multi-line)
              label, and the active choice highlighted. */}
          {uses.length > 0 ? (
            <div className="dp-drawer__field">
              <label className="dp-drawer__label">
                Alarm Behavior <span className="dp-drawer__required">*</span>
              </label>
              <div className="dp-drawer__radio-group" role="radiogroup" aria-label="Alarm Behavior">
                {uses.map((u) => {
                  const selected = String(useId) === String(u.inovonics_device_use_id);
                  return (
                    <label
                      key={u.inovonics_device_use_id}
                      className={`dp-drawer__radio-option${selected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="ad-alarm-behavior"
                        value={u.inovonics_device_use_id}
                        checked={selected}
                        onChange={(e) => setUseId(e.target.value)}
                        className="dp-drawer__radio-input"
                      />
                      <span className="dp-drawer__radio-text">{u.option_text}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Multi-add: failure summary (shown after a partial-failure batch) */}
          {multiMode && batchFailures.length > 0 ? (
            <div className="dp-drawer__field">
              <p className="dp-drawer__error" style={{ marginBottom: 'var(--space-1)' }}>
                {batchFailures.length} device{batchFailures.length === 1 ? '' : 's'} failed to add. Review and retry below.
              </p>
              <ul className="dp-batch-failures">
                {batchFailures.map((f) => (
                  <li key={f.serial}>
                    <strong>{f.serial}</strong> &mdash; {f.error}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Footer */}
          <div className="dp-drawer__footer">
            <button
              type="button"
              onClick={onClose}
              className="dp-btn dp-btn--secondary"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || saving}
              className="dp-btn dp-btn--primary"
            >
              {multiMode
                ? saving
                  ? `Adding ${batchProgress.current} of ${batchProgress.total}...`
                  : serials.length > 0
                    ? `Add ${serials.length} Device${serials.length === 1 ? '' : 's'}`
                    : 'Add Devices'
                : saving ? 'Adding...' : 'Add Device'}
            </button>
          </div>
        </form>
      </Drawer>

      {/* Image preview lightbox — click anywhere to dismiss */}
      {isOpen && previewOpen && previewImageUrl && !previewError ? (
        <div
          className="dp-drawer__device-preview-lightbox"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
          aria-label="Device image preview"
        >
          <img
            src={`/static/image/${previewImageUrl}`}
            alt={selectedType?.device_type_name || ''}
            onError={() => { setPreviewError(true); setPreviewOpen(false); }}
          />
        </div>
      ) : null}
    </>
  );
};

export default AddDeviceDrawer;
